import { useNavigate } from 'react-router-dom';
import { isApiError } from '../../api/client';
import { partner } from '../../api/partner';
import type { CaseItem, ReceivedHandover, StepKey, TripRow } from '../../api/types';
import { canWriteCases, isDriver, useSession } from '../../app/session';
import { Tile } from '../../components/BigButton';
import { ErrorBlock } from '../../components/ErrorBlock';
import { Loading } from '../../components/Loading';
import { PageHeader } from '../../components/PageHeader';
import { useT } from '../../i18n';
import { isToday, useFormat } from '../../lib/format';
import { useAsync, useOnVisible } from '../../lib/useAsync';
import { HandoverCard } from '../handovers/HandoversPage';
import { stepLabel } from '../steps/StepPanel';

/** The next big button a handover needs, from its progress stage. */
export function nextStep(h: ReceivedHandover): StepKey | null {
  switch (h.progress?.stage) {
    case undefined:
    case 'accepted':
      return 'picked_up';
    case 'picked_up':
      return 'border_crossed';
    case 'in_transit':
      return 'delivered';
    default:
      return null;
  }
}

async function safe<T>(p: Promise<T>, empty: T): Promise<T> {
  try {
    return await p;
  } catch (e) {
    if (isApiError(e) && (e.status === 403 || e.status === 404)) return empty;
    throw e;
  }
}

interface HomeData {
  handovers: ReceivedHandover[];
  cases: CaseItem[];
  trips: TripRow[];
}

/** «Сьогодні»: what needs a step now, big counters that open the lists. */
export function HomePage() {
  const t = useT();
  const nav = useNavigate();
  const f = useFormat();
  const me = useSession((s) => s.me);
  const driver = isDriver(me);
  const q = useAsync<HomeData>(async () => {
    const [handovers, cases, trips] = await Promise.all([
      safe(partner.handovers(), [] as ReceivedHandover[]),
      safe(partner.cases({ open: true }).then((r) => r.items), [] as CaseItem[]),
      safe(partner.trips(driver ? { driver: 'me' } : undefined), [] as TripRow[]),
    ]);
    return { handovers, cases, trips };
  }, [driver]);
  useOnVisible(() => void q.reload());

  const d = q.data;
  const inWork = (d?.handovers ?? []).filter((h) => h.status === 'accepted');
  const needStep = inWork.filter((h) => nextStep(h));
  const offered = (d?.handovers ?? []).filter((h) => h.status === 'offered');
  const tripsToday = (d?.trips ?? []).filter(
    (x) => x.status === 'in_progress' || (x.status === 'planned' && (isToday(x.starts_at) || isToday(x.ends_at))),
  );
  // no trip today → say when the next one is, instead of a bare «0» next to a planned trip
  const upcoming = (d?.trips ?? [])
    .filter((x) => x.status === 'planned' && x.starts_at && new Date(x.starts_at).getTime() > Date.now())
    .sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at)));
  const tripsTile =
    tripsToday.length || !upcoming.length
      ? { value: tripsToday.length, label: t('home.tripsToday') }
      : { value: upcoming.length, label: t('home.tripsNext', { date: f.date(upcoming[0]!.starts_at, false) }) };
  const name = me?.user?.full_name?.split(/\s+/)[0] ?? '';

  return (
    <>
      <PageHeader title={name ? t('home.hello', { name }) : t('home.today')} sub={me?.organization?.name} />
      {q.loading && !d ? <Loading /> : null}
      {q.error ? <ErrorBlock error={q.error} onRetry={() => void q.reload()} /> : null}
      {d ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Tile value={inWork.length} label={t('home.needStep')} onClick={() => nav('/handovers?tab=accepted')} />
            <Tile value={offered.length} label={t('home.offered')} onClick={() => nav('/handovers?tab=offered')} />
            <Tile value={d.cases.length} label={t('home.cases')} onClick={() => nav('/cases')} />
            <Tile value={tripsTile.value} label={tripsTile.label} onClick={() => nav('/trips')} />
          </div>
          <h2 className="sec">{t('home.next')}</h2>
          {needStep.length || offered.length ? (
            <ul className="space-y-3">
              {needStep.map((h) => (
                <li key={h.id}>
                  <HandoverCard h={h} hint={t('home.stepHint', { step: t(stepLabel(nextStep(h)!)) })} />
                </li>
              ))}
              {offered.map((h) => (
                <li key={h.id}>
                  <HandoverCard h={h} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-lab">{t('home.allClear')}</p>
          )}
          {canWriteCases(me) ? (
            <button type="button" className="btn mt-5 w-full" onClick={() => nav('/cases/new')}>
              + {t('home.newCase')}
            </button>
          ) : null}
        </>
      ) : null}
    </>
  );
}
